import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../auth/firebaseAuth';

import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { AnalyticsEvents } from '../../analytics/firebaseAnalytics';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';

// Inline edit modal extracted; property type and amenities lists now referenced in the modal component
import ManagedPropertiesTabs from './ManagedPropertiesTabs';
import ManagedPropertiesStats from './ManagedPropertiesStats';
import AddTenant from '../modals/AddTenant';
import { ManagedProperty } from '../property/property-card/PropertyCardTypes';
import EditManagedPropertyModal from './EditManagedPropertyModal';
import type {
  ListingIntent,
  PropertyProgressStatus,
} from '../property/property-card/PropertyCardTypes';

const FUNCTION_NAMES = {
  GET: 'getManagedProperties',
  UPDATE: 'updateManagedProperty',
  ARCHIVE: 'archiveManagedProperty',
  UNARCHIVE: 'unarchiveManagedProperty',
} as const;

type ManagedPropertyFormState = {
  address: string;
  type: ManagedProperty['type'];
  bedrooms: number;
  bathrooms: number;
  size?: number;
  berRating: string;
  amenities: string[];
  notes: string;
  status: 'managed' | 'managed-archived';
  listingIntent: ListingIntent;
  propertyProgressStatus?: PropertyProgressStatus;
  archiveReason?: string;
};

type ManagedPropertyPortfolioProps = {
  showList?: boolean;
};

const ManagedPropertyPortfolio: React.FC<ManagedPropertyPortfolioProps> = ({
  showList = false,
}) => {
  const { currentUser } = useAuth();
  const analytics = useAnalyticsWithConsent();
  const navigate = useNavigate();
  const [properties, setProperties] = useState<ManagedProperty[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<'active' | 'archived'>('active');
  const [selectedProperty, setSelectedProperty] = useState<ManagedProperty | null>(null);
  const [editForm, setEditForm] = useState<ManagedPropertyFormState | null>(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [savingChanges, setSavingChanges] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [unarchivingPropertyId, setUnarchivingPropertyId] = useState<number | null>(null);
  const [addTenantModalOpen, setAddTenantModalOpen] = useState(false);
  const [propertyForTenant, setPropertyForTenant] = useState<ManagedProperty | null>(null);

  const fetchProperties = useCallback(
    async (showFullLoader = false) => {
      if (!currentUser) return;
      setError(null);
      if (showFullLoader) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }

      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/${FUNCTION_NAMES.GET}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || 'Failed to load properties');
        }

        setProperties(payload.data || []);
      } catch (err: any) {
        console.error('Failed to load managed properties', err);
        setError(err.message || 'Failed to load managed properties');
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    },
    [currentUser]
  );

  useEffect(() => {
    if (!currentUser) return;
    fetchProperties(true);
  }, [currentUser, fetchProperties]);

  const hasLoggedPortfolioViewRef = useRef<string | null>(null);
  useEffect(() => {
    if (!currentUser) return;
    if (hasLoggedPortfolioViewRef.current === currentUser.uid) {
      return;
    }

    analytics.logEvent(AnalyticsEvents.MANAGED_PROPERTY_PORTFOLIO_VIEWED, {
      user_id: currentUser.uid,
    });
    hasLoggedPortfolioViewRef.current = currentUser.uid;
  }, [analytics, currentUser]);

  const activeProperties = useMemo(
    () => properties.filter((property) => property.status === 'managed'),
    [properties]
  );

  const archivedProperties = useMemo(
    () => properties.filter((property) => property.status === 'managed-archived'),
    [properties]
  );

  // Stats are computed inside ManagedPropertiesStats component

  const displayedProperties = selectedTab === 'active' ? activeProperties : archivedProperties;

  const openEditModal = (property: ManagedProperty) => {
    setSelectedProperty(property);
    setEditForm({
      address: property.address || property.location || '',
      type: property.type,
      bedrooms: property.bedrooms || 0,
      bathrooms: property.bathrooms || 0,
      size: property.size,
      berRating: property.berRating || '',
      amenities: property.amenities || [],
      notes: property.notes || '',
      status: property.status,
      listingIntent: (property as any).listingIntent ?? 'rent',
      propertyProgressStatus: (property as any).propertyProgressStatus,
      archiveReason: property.archiveReason || '',
    });
    setEditModalOpen(true);

    // Track feature usage
    analytics.trackInteraction('managed_property_edit_opened', {
      property_id: property.id,
      property_address: property.address,
      feature: 'edit_property',
    });
  };

  const closeEditModal = () => {
    setSelectedProperty(null);
    setEditForm(null);
    setSavingChanges(false);
    setEditModalOpen(false);
  };

  const openArchiveModal = (property: ManagedProperty) => {
    setSelectedProperty(property);
    setArchiveReason(property.archiveReason || '');
    setArchiveModalOpen(true);

    // Track feature usage
    analytics.trackInteraction('managed_property_archive_opened', {
      property_id: property.id,
      property_address: property.address,
      feature: 'archive_property',
    });
  };

  const closeArchiveModal = () => {
    setArchiveReason('');
    setSelectedProperty(null);
    setArchiving(false);
    setArchiveModalOpen(false);
  };

  const openAddTenantModal = (property: ManagedProperty) => {
    setPropertyForTenant(property);
    setAddTenantModalOpen(true);

    // Track feature usage
    analytics.trackInteraction('managed_property_add_tenant_opened', {
      property_id: property.id,
      property_address: property.address,
      feature: 'add_tenant',
    });
  };

  const closeAddTenantModal = () => {
    setPropertyForTenant(null);
    setAddTenantModalOpen(false);
  };

  const handleEditFieldChange = (field: keyof ManagedPropertyFormState, value: any) => {
    if (!editForm) return;
    setEditForm({
      ...editForm,
      [field]: value,
    });
  };

  const toggleAmenity = (amenity: string) => {
    if (!editForm) return;
    const hasAmenity = editForm.amenities.includes(amenity);
    const updatedAmenities = hasAmenity
      ? editForm.amenities.filter((item) => item !== amenity)
      : [...editForm.amenities, amenity];
    setEditForm({ ...editForm, amenities: updatedAmenities });
  };

  const submitEditChanges = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!currentUser || !selectedProperty || !editForm) return;

    setSavingChanges(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/${FUNCTION_NAMES.UPDATE}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          propertyId: selectedProperty.id,
          updates: (() => {
            const normalizedSize =
              typeof editForm.size === 'number' && editForm.size > 0
                ? Number(editForm.size)
                : undefined;
            return {
              address: editForm.address.trim(),
              type: editForm.type,
              bedrooms: Number(editForm.bedrooms),
              bathrooms: Number(editForm.bathrooms),
              size: normalizedSize,
              berRating: editForm.berRating,
              amenities: editForm.amenities,
              notes: editForm.notes,
              listingIntent: editForm.listingIntent,
              propertyProgressStatus: editForm.propertyProgressStatus,
            };
          })(),
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to update property');
      }

      // If status changed, archive or unarchive accordingly
      const statusChanged = editForm.status !== selectedProperty.status;
      if (statusChanged) {
        if (editForm.status === 'managed-archived') {
          const archiveResp = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/${FUNCTION_NAMES.ARCHIVE}`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              propertyId: selectedProperty.id,
              archiveReason: (editForm.archiveReason || '').trim(),
            }),
          });
          const archivePayload = await archiveResp.json();
          if (!archiveResp.ok) {
            throw new Error(archivePayload?.error || 'Failed to archive property');
          }
          analytics.logEvent(AnalyticsEvents.ARCHIVE_MANAGED_PROPERTY, {
            property_id: selectedProperty.id,
          });
        } else if (editForm.status === 'managed') {
          const unarchiveResp = await fetch(
            `${CLOUD_FUNCTIONS_BASE_URL}/${FUNCTION_NAMES.UNARCHIVE}`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ propertyId: selectedProperty.id }),
            }
          );
          const unarchivePayload = await unarchiveResp.json();
          if (!unarchiveResp.ok) {
            throw new Error(unarchivePayload?.error || 'Failed to unarchive property');
          }
          analytics.logEvent(AnalyticsEvents.UNARCHIVE_MANAGED_PROPERTY, {
            property_id: selectedProperty.id,
          });
        }
      }

      analytics.logEvent(AnalyticsEvents.UPDATE_MANAGED_PROPERTY, {
        property_id: selectedProperty.id,
        property_type: editForm.type,
      });

      toast.success('Managed property updated');
      closeEditModal();
      await fetchProperties();
    } catch (err: any) {
      console.error('Failed to update property', err);
      toast.error(err.message || 'Failed to update property');
    } finally {
      setSavingChanges(false);
    }
  };

  const submitArchiveProperty = async () => {
    if (!currentUser || !selectedProperty) return;

    setArchiving(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/${FUNCTION_NAMES.ARCHIVE}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          propertyId: selectedProperty.id,
          archiveReason: archiveReason.trim(),
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to archive property');
      }

      analytics.logEvent(AnalyticsEvents.ARCHIVE_MANAGED_PROPERTY, {
        property_id: selectedProperty.id,
      });

      toast.success('Property archived successfully');
      closeArchiveModal();
      await fetchProperties();
    } catch (err: any) {
      console.error('Failed to archive property', err);
      toast.error(err.message || 'Failed to archive property');
    } finally {
      setArchiving(false);
    }
  };

  const submitUnarchiveProperty = async (property: ManagedProperty) => {
    if (!currentUser) return;

    setUnarchivingPropertyId(property.id);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/${FUNCTION_NAMES.UNARCHIVE}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          propertyId: property.id,
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to unarchive property');
      }

      analytics.logEvent(AnalyticsEvents.UNARCHIVE_MANAGED_PROPERTY, {
        property_id: property.id,
      });

      toast.success('Property moved back to active portfolio');
      await fetchProperties();
    } catch (err: any) {
      console.error('Failed to unarchive property', err);
      toast.error(err.message || 'Failed to unarchive property');
    } finally {
      setUnarchivingPropertyId(null);
    }
  };

  if (!currentUser) {
    return null;
  }

  return (
    <section className="py-12 bg-gradient-to-b from-white to-blue-50">
      <div className="container mx-auto px-4">
        <div className="flex flex-col gap-6 mb-8 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider text-blue-500">
              Managed Portfolio
            </p>
            <h2 className="text-3xl font-bold text-gray-900">Private Property Dashboard</h2>
            <p className="text-gray-600 mt-1">
              View, edit, and archive properties that are hidden from the public marketplace.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => fetchProperties()}
              className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm transition hover:bg-blue-50"
              disabled={isRefreshing}
            >
              <ArrowPathIcon className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <Link
              to="/landlord-toolkit/add-property"
              className="inline-flex items-center gap-2 no-underline rounded-xl bg-gradient-to-r from-green-500 to-green-600 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-green-500/40 transition hover:scale-[1.01]"
            >
              Add Property
            </Link>
          </div>
        </div>

        {/* Stats */}
        <ManagedPropertiesStats
          activeProperties={activeProperties}
          archivedProperties={archivedProperties}
          onManagedUnitsClick={() => navigate('/landlord-toolkit/managed-properties')}
        />

        {showList && (
          <ManagedPropertiesTabs
            selectedTab={selectedTab}
            onTabChange={(tab) => setSelectedTab(tab)}
            activeCount={activeProperties.length}
            archivedCount={archivedProperties.length}
            loading={loading}
            error={error}
            displayedProperties={displayedProperties}
            unarchivingPropertyId={unarchivingPropertyId}
            openAddTenantModal={openAddTenantModal}
            openEditModal={openEditModal}
            openArchiveModal={openArchiveModal}
            submitUnarchiveProperty={submitUnarchiveProperty}
          />
        )}
      </div>

      {/* Add Tenant Modal */}
      {propertyForTenant && (
        <AddTenant
          isOpen={addTenantModalOpen}
          onClose={closeAddTenantModal}
          propertyId={propertyForTenant.id}
          propertyTitle={propertyForTenant.title || propertyForTenant.type}
          onSuccess={() => {
            // Maybe refresh properties or show success message
            // The modal itself shows a toast
          }}
        />
      )}

      {/*  Edit Managed Property */}
      {editModalOpen && selectedProperty && editForm && (
        <EditManagedPropertyModal
          isOpen={editModalOpen}
          property={selectedProperty}
          form={editForm}
          savingChanges={savingChanges}
          onClose={closeEditModal}
          onChange={handleEditFieldChange}
          onToggleAmenity={toggleAmenity}
          onSubmit={submitEditChanges}
        />
      )}

      {/* Archive Modal */}
      {archiveModalOpen && selectedProperty && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl">
            <div className="border-b border-gray-100 px-6 py-4">
              <h3 className="text-xl font-bold text-gray-900">Archive Property</h3>
              <p className="text-sm text-gray-500">
                This property will remain private but hidden from your active dashboard.
              </p>
            </div>
            <div className="px-6 py-6">
              <p className="text-sm font-semibold text-gray-700">Optional archive reason</p>
              <textarea
                value={archiveReason}
                onChange={(event) => setArchiveReason(event.target.value)}
                rows={4}
                className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-100"
                placeholder="e.g., Property sold, no longer rented"
              />
              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeArchiveModal}
                  className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={submitArchiveProperty}
                  className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-rose-500/40 transition hover:scale-[1.01]"
                  disabled={archiving}
                >
                  {archiving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                  Archive Property
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ManagedPropertyPortfolio;
